import api from './api';
import type { VenueLocation } from '../types/venue';

/**
 * WAREHOUSE-ADDRESS.1 — locations, addressed by the warehouse that holds them.
 *
 * The venue-scoped writes are retired. They never took a warehouse: creating a
 * location called ensureVenueWarehouse and landed in the venue's PRIMARY
 * whichever warehouse the operator meant, which is why adding "General Stock"
 * to Alfred's reported that it already existed — it went to Main Store.
 *
 * Reads stay on venueLocationService. "Every location this venue may act on"
 * spans several warehouses and is answered from entitlement, so there is no one
 * warehouse to address it to.
 */

const base = (warehouseId: string) => `/admin/warehouse/${warehouseId}/locations`;

export interface WarehouseLocationsResponse {
  warehouseId: string;
  /** What the caller may do here, resolved server-side. */
  permissions: string[];
  locations: VenueLocation[];
}

const warehouseLocationService = {
  /** Every location in one warehouse, with what the caller may do to them. */
  list: async (warehouseId: string, includeInactive = false): Promise<WarehouseLocationsResponse> => {
    const res = await api.get(base(warehouseId), {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return res.data;
  },

  create: async (
    warehouseId: string,
    payload: { name: string; type?: string },
  ): Promise<VenueLocation> => {
    const res = await api.post(base(warehouseId), payload);
    return res.data;
  },

  update: async (
    warehouseId: string,
    locationId: string,
    payload: { name?: string; active?: boolean },
  ): Promise<VenueLocation> => {
    const res = await api.put(`${base(warehouseId)}/${locationId}`, payload);
    return res.data;
  },

  /**
   * Deactivate. There is no DELETE: the venue route's "delete" was always a
   * soft deactivate, and saying so in the call is better than a verb that
   * implies the row goes away.
   *
   * Carries the same refusals it always did — open draft documents, and the
   * last default while other locations are still active — so callers must
   * surface the 409 message rather than assume success.
   */
  deactivate: (warehouseId: string, locationId: string): Promise<VenueLocation> =>
    warehouseLocationService.update(warehouseId, locationId, { active: false }),
};

export default warehouseLocationService;
